import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TextInputModalUI Component
 * A pure UI component for displaying a text input modal and handling user interaction.
 *
 * @example
 * ```tsx
 * <TextInputModalUI
 *   isOpen={true}
 *   title="Translate Text"
 *   value="Text to translate"
 *   onChange={handleChange}
 *   onSubmit={handleSubmit}
 *   onClose={handleClose}
 * />
 * ```
 */
import React, { useMemo } from 'react';
import { Modal } from '../../core/Modal/index.js';
import { ModelSelector } from '../../core/ModelSelector/index.js';
import { LanguageSelectorContainer } from '../../composite/LanguageSelectorContainer/index.js';
import { Button } from '../../core/Button/index.js';
import { TextArea } from '../../core/TextArea/index.js';
/**
 * Text Input Modal UI Component.
 * A presentational component with no business logic.
 */
export const TextInputModalUI = React.memo(({ isOpen, title, value, placeholder = 'Enter or paste text to savor multilingual reading', onChange, onSubmit, onClose, isLoading = false, error, submitButtonText = 'Submit', cancelButtonText = 'Cancel', submitDisabled = false, model, onModelChange, showModelSelector = true, models = [], documentIdentity, showLanguageSelector = true, sourceLanguage, targetLanguage, onSourceLanguageChange, onTargetLanguageChange, closeOnBackdropClick = true, }) => {
    // Use useMemo to optimize rendering of child components
    const languageSelectors = useMemo(() => {
        if (!showLanguageSelector)
            return null;
        return (_jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "block text-sm font-medium text-static-beach-orange mb-1", children: "Language Settings" }), _jsxs("div", { className: "flex gap-4", children: [_jsx("div", { className: "flex-1", children: _jsx(LanguageSelectorContainer, { type: "source", identity: documentIdentity, modelId: model, label: "Source Language", value: sourceLanguage, onChange: onSourceLanguageChange }) }), _jsx("div", { className: "flex-1", children: _jsx(LanguageSelectorContainer, { type: "target", identity: documentIdentity, modelId: model, label: "Target Language", value: targetLanguage, onChange: onTargetLanguageChange }) })] })] }));
    }, [
        documentIdentity,
        model,
        sourceLanguage,
        targetLanguage,
        onSourceLanguageChange,
        onTargetLanguageChange,
        showLanguageSelector,
    ]);
    const modelSelector = useMemo(() => {
        if (!showModelSelector)
            return null;
        return (_jsxs("div", { className: "mt-4 mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-static-beach-orange mb-1", children: "Model Settings" }), models.length > 0 ? (_jsx(ModelSelector, { value: model, onChange: onModelChange, label: "Select Translation Model", models: models, variant: "cards" })) : (_jsx("div", { className: "p-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-center", children: "No models available" }))] }));
    }, [model, models, onModelChange, showModelSelector]);
    return (_jsxs(Modal, { isOpen: isOpen, onClose: onClose, title: title, closeOnBackdropClick: closeOnBackdropClick, children: [_jsxs("div", { className: "p-4", children: [_jsx(TextArea, { value: value, onChange: onChange, placeholder: placeholder, rows: 6, error: !!error, autoFocus: true, className: "w-full min-h-[120px]" }), error && _jsx("div", { className: "text-sm text-red-500 mt-1", children: error }), languageSelectors, modelSelector] }), _jsxs("div", { className: "px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200", children: [_jsx(Button, { variant: "primary", onClick: onSubmit, disabled: submitDisabled || isLoading || models.length === 0, loading: isLoading, children: submitButtonText }), _jsx(Button, { variant: "secondary", onClick: onClose, disabled: isLoading, className: "mr-2", children: cancelButtonText })] })] }));
}, (prevProps, nextProps) => {
    // Custom comparison function to re-render only when key props change
    return (prevProps.isOpen === nextProps.isOpen &&
        prevProps.value === nextProps.value &&
        prevProps.error === nextProps.error &&
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.model === nextProps.model &&
        prevProps.sourceLanguage === nextProps.sourceLanguage &&
        prevProps.targetLanguage === nextProps.targetLanguage &&
        prevProps.submitDisabled === nextProps.submitDisabled &&
        JSON.stringify(prevProps.models) === JSON.stringify(nextProps.models));
});
TextInputModalUI.displayName = 'TextInputModalUI';
export default TextInputModalUI;
//# sourceMappingURL=TextInputModalUI.js.map